import { useEffect, useState } from 'react';
import { MeetingAttendeeDetailsListType, MeetingOrganizationDetailsListType, ReplaysListResponseType } from '../lib/api';
import { MATCHED_KEY_INDEX } from '../lib/constants';

type TOrganizationsOrAttendees = MeetingOrganizationDetailsListType | MeetingAttendeeDetailsListType;
type TMatchKeys = 'name' | 'fullname';
export type IUseSearchReplaysReturnType = {
  searchValue: string;
  searchedUpcomingSessions: ReplaysListResponseType;
  searchedPastSessions: ReplaysListResponseType;
  handleSearch: (inputValue: string) => void;
}

function useSearchReplays(upcomingSessions: ReplaysListResponseType, pastSessions: ReplaysListResponseType): IUseSearchReplaysReturnType {
  const [searchValue, setValue] = useState<string>('');
  const [searchedUpcomingSessions, setSearchedUpcomingSessions] = useState<ReplaysListResponseType>([]);
  const [searchedPastSessions, setSearchedPastSessions] = useState<ReplaysListResponseType>([]);

  useEffect(() => {
    setSearchedUpcomingSessions(upcomingSessions ?? []);
    setSearchedPastSessions(pastSessions ?? []);
  }, [upcomingSessions, pastSessions]);

  const handleSearch = (inputValue: string) => {
    const value = inputValue.toUpperCase().trim();
    if (!value) {
      setValue('');
      setSearchedUpcomingSessions(upcomingSessions ?? []);
      setSearchedPastSessions(pastSessions ?? []);
      return;
    }

    setSearchedUpcomingSessions(filteredMeetings(upcomingSessions ?? [], value));
    setSearchedPastSessions(filteredMeetings(pastSessions ?? [], value));
    setValue(inputValue);
  };

  return { searchedUpcomingSessions, searchedPastSessions, handleSearch, searchValue };
}

export default useSearchReplays;

function filteredMeetings(
  meetingsList: ReplaysListResponseType,
  searchedValue: string
): ReplaysListResponseType {
  const filteredMeetingsResult: ReplaysListResponseType = [];

  for (const meeting of meetingsList) {
    const meetingTopic = meeting.topic || '';
    const isMatchTopic = meetingTopic.toUpperCase().indexOf(searchedValue) !== -1;
    if (isMatchTopic) {
      filteredMeetingsResult.push(meeting);
      continue;
    }
    const { presenterOrganizations, hostOrganizations } = meeting;
    const isMatchPresenter = filterByOrgNameOrFullname(presenterOrganizations as MeetingOrganizationDetailsListType, searchedValue, MATCHED_KEY_INDEX);
    const isMatchHost = hostOrganizations
      ? filterByOrgNameOrFullname(hostOrganizations as MeetingOrganizationDetailsListType, searchedValue, MATCHED_KEY_INDEX)
      : false;
    if (isMatchHost || isMatchPresenter) {
      filteredMeetingsResult.push(meeting);
    }
  }

  return filteredMeetingsResult;
}

const matchedKyes: TMatchKeys[] = ['name', 'fullname'];

function filterByOrgNameOrFullname<TItems extends TOrganizationsOrAttendees>(
  list: TItems,
  searchedValue: string,
  depth: number
): boolean {
  if (!list) return false;

  const matchedKey: TMatchKeys = matchedKyes[depth];
  switch (matchedKey) {
  case 'fullname':
    for (const attendee of list as MeetingAttendeeDetailsListType) {
      const fullName = attendee.firstName.toUpperCase().concat(' ', attendee.lastName.toUpperCase());
      if (fullName.indexOf(searchedValue) !== -1) {
        return true;
      }
    }
    break;

  case 'name':
    for (const organization of list as MeetingOrganizationDetailsListType) {
      const isMatchOrganizationName = organization.name.toUpperCase().indexOf(searchedValue) !== -1;
      if (isMatchOrganizationName) {
        return true;
      }

      const { attendees } = organization;
      const isMatch = filterByOrgNameOrFullname(attendees as MeetingAttendeeDetailsListType, searchedValue, depth + 1);
      if (isMatch) {
        return true;
      }
    }
    break;
  default:
    break;
  }

  return false;
}
